import { inject, InjectionToken } from '@angular/core';
import { type CanActivateFn, Router, type UrlTree } from '@angular/router';
import { intersection } from 'lodash-es';
import type { Observable } from 'rxjs';

import { getUserSelectedCompany } from '@auth/repository/auth.repository';
import { APP_ROLE, type AppRole } from '@shared/constants/ume-constants';

import { PermissionService } from '../permissions/permission.service';

export class RoleAccessGuard {
  static canActivateWithRoles(roles: AppRole[]): any {
    return new InjectionToken('AccessGuardWithRoles', {
      providedIn: 'root',
      factory: (): {
        canActivate():
          Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree;
        canActivateChild():
          Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree;
      } => {
        const router = inject(Router);
        const permissions = inject(PermissionService);

        const checkRole = (): boolean => {
          const isAdmin = permissions.isUmeOps();
          if (isAdmin) {
            return isAdmin;
          }

          const userRoles = getUserSelectedCompany().applicationRoles;
          const canAccess = Boolean(intersection(userRoles, [...roles, APP_ROLE.ADMIN]).length);

          if (!canAccess) {
            router.navigate(['/', 'restricted-user-rights']).then(() => false);
            return false;
          }

          return true;
        };

        return {
          canActivate():
            Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
            return checkRole();
          },
          canActivateChild():
            Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
            return checkRole();
          },
        };
      },
    });
  }
}

export const searchAccessGuardFn: CanActivateFn = (): boolean => {
  const router = inject(Router);

  const userRoles = getUserSelectedCompany().applicationRoles;
  const canAccess = Boolean(
    intersection(userRoles, [APP_ROLE.ADMIN, APP_ROLE.INVITE_COUNTERPARTIES]).length
  );

  if (!canAccess) {
    router.navigate(['/', 'restricted-user-rights']).then(() => false);
    return false;
  }

  return true;
};
